<template>
  <section class="section" id="store-prices-panel">
    <div
      class="container"
      :style="hasChangesToSave ? 'padding-bottom: 3rem' : ''"
    >
      <b-loading
        :is-full-page="true"
        :active="isLoading"
        :can-cancel="false"
      ></b-loading>

      <h1 class="title has-text-centered is-spaced">
        <go-to-dashboard-button />
        {{ $t("storePrices.title") }}
        <button class="button action-button" @click="confirmGetCatalog">
          <b-icon icon="sync-alt" />
        </button>

        <button
          class="button action-button"
          :disabled="products.length === 0"
          :title="$t('storePrices.downloadReport')"
          :aria-label="$t('storePrices.downloadReport')"
          @click="downloadReport"
        >
          <b-icon icon="save" />
        </button>
      </h1>

      <h2
        v-if="products.length === 0 && !isLoading"
        class="subtitle has-text-centered"
      >
        {{ $t("products.noProductsRegisteredAtThisMoment") }}
      </h2>

      <store-prices-table
        v-else
        :products="products"
        :saving-cost-barcode="savingCostBarcode"
        @update-store-price="updateStorePrice"
        @update-cost="savePurchase"
      />
    </div>

    <div
      v-if="hasChangesToSave"
      class="has-text-centered"
      style="position: fixed; bottom: 0; left: 0; width: 100%; background-color: white; margin-left: -1rem; border-top: 1px solid black;"
    >
      <button
        class="button"
        :style="{
          backgroundColor: $primaryColor,
          color: $primaryColorContrast
        }"
        style="margin: 1rem"
        @click="saveChanges"
      >
        {{ $t("products.saveChanges") }}
      </button>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";
import { mask } from "../../mixins/mask";
import storePricesTable from "./StorePricesTable";
import goToDashboardButton from "../common/GoToDashboardButton";

export default {
  mixins: [common, mask],

  components: { storePricesTable, goToDashboardButton },

  data() {
    return {
      isLoading: false,
      products: [],
      storePriceChanges: [],
      savingCostBarcode: ""
    };
  },

  computed: {
    ...mapGetters(["purchases"]),

    hasChangesToSave() {
      return this.storePriceChanges.length > 0;
    },

    // O custo vive na tabela do franqueado, e não no catálogo de cada loja,
    // então vale para o produto inteiro nesta tela. O módulo de compras já
    // entrega o valor em reais.
    costsByBarcode() {
      return this.purchases.reduce((acc, purchase) => {
        acc[purchase.b] = purchase.lc;
        return acc;
      }, {});
    }
  },

  methods: {
    getCatalog() {
      this.isLoading = true;

      return this.$store
        .dispatch("getStorePrices")
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
            return;
          }

          this.products = this.buildProducts(res.data.payload);
          this.storePriceChanges = [];
        })
        .catch(() => this.alertError({ message: "UNEXPECTED_ERROR" }))
        .finally(() => {
          this.isLoading = false;
        });
    },

    // Cada produto vira uma linha com a lista das lojas que o têm. Os campos
    // `p`, `m` e `uniform` são derivados dessa lista, mas ficam na linha para
    // que a tabela consiga ordenar por preço e por margem.
    buildProducts({ stores, products }) {
      const storeNames = (stores || []).reduce((acc, store) => {
        acc[store.storeId] = store.storeName;
        return acc;
      }, {});

      return (products || []).map(product => {
        const storePrices = Object.keys(product.prices)
          .map(storeId => ({
            storeId,
            storeName: storeNames[storeId] || "",
            price: product.prices[storeId] / 100,
            savedPrice: product.prices[storeId] / 100,
            // O campo de dinheiro guarda o valor formatado por conta própria e
            // não reage a um valor devolvido pelo código, então desfazer uma
            // edição recusada exige remontá-lo. Ver `revertPrice` na tabela.
            revertToken: 0
          }))
          .sort((a, b) => a.storeName.localeCompare(b.storeName, "pt-BR"));

        const prices = storePrices.map(store => store.price);
        const lowest = Math.min(...prices);
        const highest = Math.max(...prices);
        const cost = this.costsByBarcode[product.b] || 0;

        return {
          b: product.b,
          n: product.n,
          lc: cost,
          storePrices,
          uniform: lowest === highest,
          // Com preços diferentes o campo parte do maior preço praticado.
          p: highest,
          revertToken: 0,
          m: lowest === highest && cost > 0 ? (lowest - cost) / lowest : null
        };
      });
    },

    // O relatório sai do que está carregado, então alterações ainda não salvas
    // ficam de fora. Baixar com pendências entregaria preços que não valem.
    downloadReport() {
      if (this.hasChangesToSave) {
        this.$buefy.dialog.confirm({
          message: this.$t("storePrices.downloadWithPendingChanges"),
          confirmText: this.$t("common.yes"),
          cancelText: this.$t("common.cancel"),
          type: "is-primary",
          onConfirm: () => this.dispatchReport()
        });
        return;
      }

      return this.dispatchReport();
    },

    dispatchReport() {
      this.isLoading = true;

      return this.$store
        .dispatch("downloadStorePricesReport")
        .catch(() => this.alertError({ message: "UNEXPECTED_ERROR" }))
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmGetCatalog() {
      if (!this.hasChangesToSave) return this.getCatalog();

      this.$buefy.dialog.confirm({
        message: this.$t("products.updatingWillLooseChanges"),
        confirmText: this.$t("products.okUpdate"),
        cancelText: this.$t("common.cancel"),
        type: "is-primary",
        onConfirm: () => this.getCatalog()
      });
    },

    getPurchases() {
      return this.$store
        .dispatch("getAllPurchases")
        .then(res => {
          if (res.data.result === "error") this.alertError(res.data);
        })
        .catch(() => this.alertError({ message: "UNEXPECTED_ERROR" }));
    },

    // A tabela avisa quando um preço volta ao valor que está salvo, e nesse
    // caso a alteração sai da lista para não gerar log de algo que não mudou.
    updateStorePrice(change) {
      const index = this.storePriceChanges.findIndex(
        c => c.barcode === change.barcode && c.storeId === change.storeId
      );

      if (change.isSavedValue) {
        if (index > -1) this.storePriceChanges.splice(index, 1);
        return;
      }

      if (index > -1) {
        this.storePriceChanges[index].p = change.p;
      } else {
        this.storePriceChanges.push({
          barcode: change.barcode,
          storeId: change.storeId,
          p: change.p
        });
      }
    },

    savePurchase(change) {
      this.savingCostBarcode = change.barcode;

      this.$store
        .dispatch("savePurchase", {
          product: { b: change.barcode, n: change.name, lc: change.lc }
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
            return;
          }

          this.$buefy.toast.open({
            message: this.$t("products.changesSaved"),
            type: "is-success",
            position: "is-bottom",
            duration: 3000
          });
        })
        .catch(() => this.alertError({ message: "UNEXPECTED_ERROR" }))
        .finally(() => {
          this.savingCostBarcode = "";
        });
    },

    saveChanges() {
      this.isLoading = true;

      this.$store
        .dispatch("updateProductStorePrices", {
          changes: this.storePriceChanges
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
            return;
          }

          this.$buefy.toast.open({
            message: this.$t("products.changesSaved"),
            type: "is-success",
            position: "is-bottom",
            duration: 5000
          });

          this.alertBlockedStores(res);
          this.getCatalog();
        })
        .catch(() => this.alertError({ message: "UNEXPECTED_ERROR" }))
        .finally(() => {
          this.isLoading = false;
        });
    },

    // Uma loja em processamento não recebe a alteração de preço, e o
    // franqueado precisa saber quais ficaram de fora para tentar de novo.
    alertBlockedStores(res) {
      const blockedStores =
        (res.data.payload && res.data.payload.blockedStores) || [];

      if (blockedStores.length === 0) return;

      this.$buefy.dialog.alert({
        title: this.$t("products.attention"),
        message: `${this.$t(
          "products.storePricesNotUpdated"
        )}<br/><br/><b>${blockedStores.join("<br/>")}</b>`,
        type: "is-warning",
        hasIcon: true,
        icon: "exclamation-triangle",
        iconPack: "fa"
      });
    }
  },

  mounted() {
    // O custo alimenta a margem de cada linha, então chega antes do catálogo.
    this.getPurchases().then(() => this.getCatalog());
  }
};
</script>

<style scoped>
.action-button {
  margin-left: 0.5rem;
}

#store-prices-panel {
  margin-top: 3rem;
  min-height: 94vh;
  padding: 2rem 1rem;
  background-color: hsl(0, 0%, 96%);
}
</style>
